import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquarePlus, Loader2, GitCompare, History } from "lucide-react";
import { VersionCompare } from "@/components/VersionCompare";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const LABEL = { open: "Submitted", in_progress: "In progress", resolved: "Done" };

export const PortalRevisions = ({ studyId, sections, study }) => {
  const [cmp, setCmp] = useState(null);
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState("general");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [revs, setRevs] = useState([]);
  useEffect(() => { api.get(`/portal/studies/${studyId}/revisions`).then((r) => setRevs(r.data)).catch(() => {}); }, [studyId]);

  const submit = async () => {
    if (message.trim().length < 3) return toast.error("Please describe the changes you need");
    setBusy(true);
    try {
      const r = await api.post(`/portal/studies/${studyId}/revisions`, { section: section === "general" ? "" : section, message });
      setRevs((x) => [r.data, ...x]);
      setOpen(false); setMessage(""); setSection("general");
      toast.success("Request sent — our team has been notified");
    } catch (e) { toast.error(errMsg(e, "Could not send request")); } finally { setBusy(false); }
  };

  return (
    <section className="no-print rounded-xl border border-slate-800 bg-slate-900/70 p-6" data-testid="portal-revisions">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-display text-lg font-semibold">Need changes?</h2><p className="text-sm text-slate-400">Send a request to our team — we'll email you when it's updated.</p></div>
        <div className="flex flex-wrap gap-2">
        <Button variant="outline" data-testid="portal-history-btn" onClick={() => setCmp({})} className="rounded-full border-slate-700 bg-transparent"><History className="h-4 w-4 mr-2" />Version history</Button>
        <Button data-testid="portal-request-changes" onClick={() => setOpen(true)} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400"><MessageSquarePlus className="h-4 w-4 mr-2" />Request changes</Button>
        </div>
      </div>
      {revs.length > 0 && (
        <div className="mt-5 divide-y divide-slate-800 border-t border-slate-800">
          {revs.map((r) => (
            <div key={r.id} className="py-3" data-testid={`portal-revision-${r.id}`}>
              <p className="text-xs text-slate-500 mb-1"><span className="font-semibold text-slate-300">{LABEL[r.status]}</span>{r.section && ` · ${r.section}`} · {r.created_at?.slice(0, 10)}</p>
              <p className="text-sm text-slate-300 whitespace-pre-line">{r.message}</p>
              {r.status === "resolved" && r.note && <p className="text-sm text-emerald-300 mt-1">Our note: {r.note}</p>}
              {r.status === "resolved" && r.before_version_id && r.after_version_id && (
                <button data-testid={`portal-compare-${r.id}`} onClick={() => setCmp({ a: r.before_version_id, b: r.after_version_id })} className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-400 hover:underline"><GitCompare className="h-3.5 w-3.5" />Compare before / after</button>
              )}
            </div>
          ))}
        </div>
      )}
      <VersionCompare open={!!cmp} onOpenChange={(o) => !o && setCmp(null)} studyId={studyId} basePath="/portal/studies" current={study} initialA={cmp?.a} initialB={cmp?.b} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700" data-testid="portal-revision-dialog">
          <DialogHeader><DialogTitle className="font-display">Request changes</DialogTitle><DialogDescription>Tell us what you'd like updated in this study.</DialogDescription></DialogHeader>
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger data-testid="portal-revision-section" className="bg-slate-950/60 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="general">Whole study / general</SelectItem>
              {sections.map((s) => <SelectItem key={s.key} value={s.title}>{s.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea data-testid="portal-revision-message" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Please use a $6,500/month lease and add a delivery revenue stream." className="bg-slate-950/60 border-slate-700" />
          <Button data-testid="portal-revision-submit" disabled={busy} onClick={submit} className="bg-emerald-500 text-emerald-950 hover:bg-emerald-400">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send request</Button>
        </DialogContent>
      </Dialog>
    </section>
  );
};
